import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ALLOWED_ACTIONS,
  useFieldInfoModalContext,
} from "context/FieldInfoModalProvider";
import { Button, MapImage, SubTitle } from "components";
import { paths } from "utils/paths";
import { toaster } from "utils/toaster";
import { fieldDimensionsRead } from "utils/fieldDimensions";
import Info, { InfoRow } from "../../common/Info";
import Layout from "../../common/FieldInfoModalLayout";
import { useMemo } from "react";
const FieldCamvioInfoView = ({ isMyField, setConfirmProps }) => {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language.startsWith("en");
  const navigate = useNavigate();
  const { field, allow, deleteField, cancelCollaboration, setToEditMode } =
    useFieldInfoModalContext();
  const {
    id,
    ownerName,
    name,
    cropVariety,
    wateringMethod,
    plantationDate,
    numberOfRoots,
    squareMeters,
    coordsForMapImage,
  } = field || {};

  const navigateToField = () => {
    navigate(paths.fields.one(id));
  };

  const handleCancelCollaboration = () => {
    cancelCollaboration({
      onError: () => {
        toaster.error({
          message: t("FieldInfo.Camvio.CancelCollab.Error"),
        });
        setConfirmProps({
          isOpen: false,
        });
      },
    });
  };

  const handleDeleteField = () => {
    deleteField({
      onError: () => {
        toaster.error({
          message: t("FieldInfo.Camvio.DeleteField.Error"),
        });
        setConfirmProps({
          isOpen: false,
        });
      },
    });
  };

  const handleRemove = () => {
    setConfirmProps({
      isOpen: true,
      title: isMyField
        ? t("FieldInfo.Camvio.DeleteField.Title")
        : t("FieldInfo.Camvio.CancelCollab.Title"),
      contentText: isMyField
        ? t("FieldInfo.Camvio.DeleteField.Text")
        : t("FieldInfo.Camvio.CancelCollab.Text"),
      onConfirm: isMyField ? handleDeleteField : handleCancelCollaboration,
    });
  };

  const allowedActions = useMemo(
    () => ({
      remove:
        !field?.isOpekepeConnected && allow.includes(ALLOWED_ACTIONS.REMOVE),
      edit: isMyField && allow.includes(ALLOWED_ACTIONS.EDIT),
      redirect: allow.includes(ALLOWED_ACTIONS.REDIRECT),
    }),
    [field, allow, isMyField]
  );

  return (
    <Layout.InfoWrapperContainer>
      <Layout.MapViewContainer>
        <MapImage
          polygon={coordsForMapImage}
          sx={{ width: "100%", height: "100%" }}
        />
      </Layout.MapViewContainer>
      <Layout.InfoContainer>
        <InfoRow>
          <Info label={t("FieldInfo.Camvio.FieldName")} value={name} />
          <Info label={t("FieldInfo.Camvio.Owner")} value={ownerName} />
          <Info
            label={t("FieldInfo.Camvio.WateringMethod")}
            value={wateringMethod}
          />
        </InfoRow>
        <div>
          <SubTitle
            text={t("FieldInfo.Camvio.Crop")}
            sx={{ color: "#818181" }}
          />
          <InfoRow>
            <Info
              label={t("FieldInfo.Camvio.PlantationDate")}
              value={plantationDate}
            />
            <Info
              label={t("FieldInfo.Camvio.CropVariety")}
              value={cropVariety}
            />
            <Info
              label={t("FieldInfo.Camvio.NumberOfRoots")}
              value={numberOfRoots}
            />
            <Info
              label={t("FieldInfo.Camvio.Area")}
              value={fieldDimensionsRead(t, isEn, squareMeters)}
            />
          </InfoRow>
        </div>
        {allowedActions.remove && (
          <Button
            color={"error"}
            corners="square"
            sx={{
              width: "fit-content",
            }}
            onClick={handleRemove}
          >
            {isMyField
              ? t("FieldInfo.Camvio.DeleteField.Title")
              : t("FieldInfo.Camvio.CancelCollab.Title")}
          </Button>
        )}
      </Layout.InfoContainer>
      {allowedActions.edit && (
        <Button
          variant="filled"
          sx={{ alignSelf: "flex-end" }}
          onClick={setToEditMode}
        >
          {t("FieldInfo.Camvio.Edit")}
        </Button>
      )}
      {allowedActions.redirect && (
        <Button onClick={navigateToField}>{t("FieldInfo.Camvio.View")}</Button>
      )}
    </Layout.InfoWrapperContainer>
  );
};

export default FieldCamvioInfoView;
